import React from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'
import {Box, Button, Flex, Link, Text, useBreakpointValue} from '@chakra-ui/react'
import PDCTitle from '../../../components/pdc-title'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import {DYNAMIC_IMAGE_WIDTHS} from '../../../constants.js'
import {useUrlFromLink} from '../../helpers/urlHelper.js'
import PDCImage from '../../../components/pdc-image/index.jsx'

/**
 * PDCCollaborationPushTile is a presentational React component designed to display a promotional tile
 * for collaboration push notifications within the Page Designer application. It features a responsive
 * layout that adapts to various screen sizes, displaying a title, subtitle, description, and a call-to-action
 * button alongside an image that changes based on the current breakpoint.
 *
 * The component supports both internal and external links, dynamically determining the link type and URL
 * based on provided props and custom configuration. It leverages utility hooks for responsive image selection
 * and URL resolution, ensuring optimal asset delivery and navigation behavior.
 *
 * Props:
 * @param {string} title - The main title text to display.
 * @param {string|number} titleHeadingLevel - The heading level for the title (e.g., 'h1', 'h2', etc.).
 * @param {string} supTitle - The subtitle or label displayed above the title, supporting HTML content.
 * @param {string} desc - The description text, supporting HTML content.
 * @param {string} linkUrl - The default URL for the call-to-action button.
 * @param {string} linkLabel - The label text for the call-to-action button.
 * @param {string} smallImage - URL for the image to display on small devices.
 * @param {string} mediumImage - URL for the image to display on medium devices.
 * @param {string} largeImage - URL for the image to display on large devices.
 * @param {string} xlargeImage - URL for the image to display on extra-large devices.
 * @param {string} xxlargeImage - URL for the image to display on double extra-large devices.
 * @param {string} imageTitle - The title attribute for the image (for accessibility).
 * @param {string} imageAlt - The alt attribute for the image (for accessibility).
 * @param {number} displayPriority - Determines the loading priority of the image.
 * @param {object} custom - Optional custom configuration object that may override default props,
 *   including custom image URLs and link information.
 *
 * Usage:
 * This component is intended for use within the Page Designer context, where it can be configured
 * with dynamic content and images to promote collaboration features or campaigns. It ensures
 * accessibility and responsiveness, and is styled to fit seamlessly within the application's design system.
 */
export const PDCCollaborationPushTile = ({
    title,
    titleHeadingLevel,
    supTitle,
    desc,
    linkUrl,
    linkLabel,
    smallImage,
    mediumImage,
    largeImage,
    xlargeImage,
    xxlargeImage,
    imageTitle,
    imageAlt,
    displayPriority,
    custom
}) => {
    const updatedUrl = useUrlFromLink(custom?.linkUrl) || linkUrl
    const linkIsExternal = custom?.url?.type === 'EXTERNAL' && !updatedUrl?.startsWith('/')

    // Get current image based on breakpoint
    const currentImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: custom?.smallImage?.disBaseLink || smallImage,
            mediumEl: custom?.mediumImage?.disBaseLink || mediumImage,
            largeEl: custom?.largeImage?.disBaseLink || largeImage,
            xlargeEl: custom?.xlargeImage?.disBaseLink || xlargeImage,
            xxlargeEl: custom?.xxlargeImage?.disBaseLink || xxlargeImage
        }),
        {
            fallback: undefined
        }
    )

    return (
        <Box
            display="flex"
            flexDirection={['column', 'column', 'row']}
            justifyContent="space-between"
            alignItems="stretch"
        >
            <Flex
                flexDirection="column"
                justifyContent="space-between"
                alignItems={'flex-start'}
                width={['100%', '100%', '50%']}
                py={'20px'}
                px={['10px', null, '20px']}
            >
                <Box pb={['24px', null, 0]}>
                    <Text
                        dangerouslySetInnerHTML={{
                            __html: supTitle
                        }}
                        variant="bodySmall"
                        color={'fullBlack'}
                        mb={['24px', null, '21px']}
                        backgroundColor={'whiteSmoke'}
                        width={'fit-content'}
                        p={'2.5px 4px'}
                    />
                    <PDCTitle
                        title={title}
                        titleFontColor={'fullBlack'}
                        titleHeadingLevel={titleHeadingLevel}
                        txtHorizontalPos={'left'}
                        textColor={'fullBlack'}
                        mb={['24px', null, '21px']}
                        size={'5xl'}
                        width={['100%', null, null, '50%']}
                    />
                    <Text
                        dangerouslySetInnerHTML={{
                            __html: desc
                        }}
                        variant="bodyLarge2"
                        width={['100%', null, null, '50%']}
                        textAlign={'left'}
                        color={'darkGray'}
                    />

                    <Button
                        as={linkIsExternal ? Link : RouteLink}
                        href={updatedUrl}
                        to={updatedUrl}
                        variant="secondary"
                        mt={[24, null, null, '44px']}
                    >
                        {linkLabel}
                    </Button>
                </Box>
            </Flex>

            <Box width={['100%', '100%', '50%']} pl={[10, null, 5]} pr={[10, null, 20]}>
                <PDCImage
                    currentImage={currentImage}
                    imageWidths={DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice}
                    imageAlt={imageAlt}
                    imageTitle={imageTitle}
                    displayPriority={displayPriority}
                    title={title}
                    skeletonProps={{aspectRatio: '139 / 180', width: '100%'}}
                />
            </Box>
        </Box>
    )
}

PDCCollaborationPushTile.propTypes = {
    category: PropTypes.string,
    title: PropTypes.string.isRequired,
    titleHeadingLevel: PropTypes.string,
    supTitle: PropTypes.string,
    desc: PropTypes.string,
    linkUrl: PropTypes.string,
    linkLabel: PropTypes.string,
    smallImage: PropTypes.object.isRequired,
    mediumImage: PropTypes.object,
    largeImage: PropTypes.object,
    xlargeImage: PropTypes.object,
    xxlargeImage: PropTypes.object,
    imageTitle: PropTypes.string,
    imageAlt: PropTypes.string,
    displayPriority: PropTypes.bool,
    nbSlides: PropTypes.number,
    custom: PropTypes.object
}
